import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, MapPin, Plane, Tag, Save, Plus, Trash2, Check, X, Lock, Eye, EyeOff, AlertCircle, Palette, Upload, Image } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  
  // Theme settings
  const [activeTheme, setActiveTheme] = useState('padrao');
  const [themeDefinitions, setThemeDefinitions] = useState({});
  const [themeImageFile, setThemeImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [currentThemeConfig, setCurrentThemeConfig] = useState(null);
  
  // Site settings
  const [blockCouponDuringPromotion, setBlockCouponDuringPromotion] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Campaign mode
  const [campaignMode, setCampaignMode] = useState(false);
  const [campaignImage, setCampaignImage] = useState('');
  const [campaignText, setCampaignText] = useState('');
  const [campaignLink, setCampaignLink] = useState('');
  const [campaignStart, setCampaignStart] = useState('');
  const [campaignEnd, setCampaignEnd] = useState('');
  const [campaignDailyDiscounts, setCampaignDailyDiscounts] = useState({});
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [cardIndicator, setCardIndicator] = useState('percentage');
  const [logoCompanionImage, setLogoCompanionImage] = useState('');

  // Fechamento automático de vendas
  const [salesClosingDays, setSalesClosingDays] = useState({
    'nacional-aereo': 7,
    'internacional-aereo': 15,
    'rodoviario': 3,
    'passeio-1-dia': 1,
  });
  const [savingClosing, setSavingClosing] = useState(false);
  
  // Custom theme editor
  const [customThemeName, setCustomThemeName] = useState('');
  const [customThemeColors, setCustomThemeColors] = useState({
    primary: '#003366', secondary: '#FFD700', background: '#ffffff',
    backgroundLight: '#f8fafc', text: '#1f2937', headerBg: '#ffffff'
  });
  const [customThemes, setCustomThemes] = useState({});
  const [savingCustomTheme, setSavingCustomTheme] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newUsername: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentAdminUser, setCurrentAdminUser] = useState('admin');
  
  // Pontos de embarque rodoviário
  const [boardingPoints, setBoardingPoints] = useState([
    'Rodoviária Novo Rio',
    'Casa do Alemão na W Luiz',
    'Prefeitura de Jardim Primavera'
  ]);

  // Cidades de embarque aéreo
  const [departureCities, setDepartureCities] = useState([
    'Rio de Janeiro',
    'São Paulo'
  ]);

  // Opções de "Incluso" padrão
  const [includedOptions, setIncludedOptions] = useState([
    { text: 'Passagens aéreas Saindo do RJ/SP', default: true },
    { text: 'Transfer Santarém x Alter (ida e volta)', default: false },
    { text: 'Translado entre os atrativos', default: true },
    { text: 'Passeio de barco', default: false },
    { text: 'Hospedagem (quarto compartilhado)', default: true },
    { text: 'Camping', default: false },
    { text: 'Passeio de 4x4', default: false },
    { text: 'Entradas dos atrativos descritos', default: true },
    { text: 'Café da manhã', default: true },
    { text: 'Guia Credenciado Cadastur', default: true },
    { text: 'Guias locais', default: false },
    { text: 'Seguro contra acidentes pessoais', default: true },
    { text: 'Brinde comemorativo', default: false }
  ]);

  // Opções de "Não Incluso" padrão
  const [notIncludedOptions, setNotIncludedOptions] = useState([
    { text: 'Alimentação extra', default: true },
    { text: 'Serviços opcionais', default: true },
    { text: 'Tudo que não estiver descrito como incluso', default: true },
    { text: 'Entradas dos atrativos não descritos', default: true }
  ]);

  // Cupons de desconto
  const [coupons, setCoupons] = useState([
    { code: 'PRIMEIRAVIAGEM', discount: 10, active: true },
    { code: 'TOINDOALI5', discount: 5, active: true }
  ]);

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }

    // Carregar configurações salvas
    const savedSettings = localStorage.getItem('adminSettings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      if (settings.boardingPoints) setBoardingPoints(settings.boardingPoints);
      if (settings.departureCities) setDepartureCities(settings.departureCities);
      if (settings.includedOptions) setIncludedOptions(settings.includedOptions);
      if (settings.notIncludedOptions) setNotIncludedOptions(settings.notIncludedOptions);
      if (settings.coupons) setCoupons(settings.coupons);
    }
    
    // Carregar configurações de tema
    loadThemeSettings();
    loadSiteSettings();
    loadCampaignSettings();
    loadCustomThemes();
  }, [navigate]);

  const loadSiteSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/themes/settings`);
      setBlockCouponDuringPromotion(response.data.block_coupon_during_promotion || false);
    } catch {}
  };

  const loadCampaignSettings = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/packages/site-settings`);
      const d = res.data || {};
      setCampaignMode(d.campaign_mode || false);
      setCampaignImage(d.campaign_image || '');
      setCampaignText(d.campaign_text || '');
      setCampaignLink(d.campaign_link || '');
      setCampaignStart(d.campaign_start ? d.campaign_start.split('T')[0] : '');
      setCampaignEnd(d.campaign_end ? d.campaign_end.split('T')[0] : '');
      const daily = {};
      (d.campaign_daily_discounts || []).forEach(e => {
        if (e?.date) daily[e.date] = Number(e.discount) || 0;
      });
      setCampaignDailyDiscounts(daily);
      setCardIndicator(d.card_indicator || 'percentage');
      if (d.sales_closing_days && typeof d.sales_closing_days === 'object') {
        setSalesClosingDays(prev => ({ ...prev, ...d.sales_closing_days }));
      }
    } catch {}
  };

  const saveClosingSettings = async () => {
    setSavingClosing(true);
    try {
      await axios.put(`${BACKEND_URL}/api/packages/site-settings`, {
        sales_closing_days: salesClosingDays,
      });
      alert('Regras de fechamento salvas!');
    } catch { alert('Erro ao salvar'); }
    finally { setSavingClosing(false); }
  };

  const saveCampaignSettings = async () => {
    setSavingCampaign(true);
    try {
      await axios.put(`${BACKEND_URL}/api/packages/site-settings`, {
        campaign_mode: campaignMode,
        campaign_image: campaignImage || null,
        campaign_text: campaignText || null,
        campaign_link: campaignLink || null,
        campaign_start: campaignStart ? new Date(campaignStart).toISOString() : null,
        campaign_end: campaignEnd ? new Date(campaignEnd).toISOString() : null,
        campaign_daily_discounts: Object.entries(campaignDailyDiscounts).map(([date, discount]) => ({ date, discount: Number(discount) || 0 }))
      });
      alert('Campanha salva!');
    } catch { alert('Erro ao salvar'); }
    finally { setSavingCampaign(false); }
  };

  const loadCustomThemes = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/themes/custom`);
      setCustomThemes(res.data || {});
    } catch {}
  };

  const saveCustomTheme = async () => {
    if (!customThemeName.trim()) { alert('Defina um nome para o tema'); return; }
    setSavingCustomTheme(true);
    const id = 'custom-' + customThemeName.toLowerCase().replace(/[^a-z0-9]/g, '-');
    try {
      await axios.post(`${BACKEND_URL}/api/themes/custom`, {
        id, theme: { name: customThemeName, ...customThemeColors }
      });
      alert(`Tema "${customThemeName}" salvo!`);
      loadCustomThemes();
      loadThemeSettings();
      setCustomThemeName('');
    } catch { alert('Erro ao salvar tema'); }
    finally { setSavingCustomTheme(false); }
  };

  const handleSaveSiteSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${BACKEND_URL}/api/themes/settings`, {
        block_coupon_during_promotion: blockCouponDuringPromotion
      });
      alert('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      alert('Erro ao salvar configurações');
    } finally {
      setSavingSettings(false);
    }
  };

  const loadThemeSettings = async () => {
    try {
      const [activeResponse, definitionsResponse, customResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/themes/active`),
        axios.get(`${BACKEND_URL}/api/themes/all`).catch(() => axios.get(`${BACKEND_URL}/api/themes/definitions`)),
        axios.get(`${BACKEND_URL}/api/themes/custom`).catch(() => ({ data: {} }))
      ]);
      
      setCurrentThemeConfig(activeResponse.data);
      setActiveTheme(activeResponse.data.theme_id || 'padrao');
      setThemeDefinitions(definitionsResponse.data);
      setCustomThemes(customResponse.data || {});
    } catch (error) {
      console.error('Erro ao carregar temas:', error);
    }
  };

  const handleThemeChange = async (themeId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/themes/set`, { theme_id: themeId });
      setActiveTheme(themeId);
      alert(`Tema "${themeDefinitions[themeId]?.name}" ativado com sucesso!`);
      loadThemeSettings();
    } catch (error) {
      console.error('Erro ao alterar tema:', error);
      alert('Erro ao alterar tema');
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${BACKEND_URL}/api/themes/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      alert('Imagem do tema carregada com sucesso!');
      loadThemeSettings();
      setThemeImageFile(null);
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      alert('Erro ao fazer upload da imagem: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!window.confirm('Remover imagem do tema?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/themes/remove-image`);
      alert('Imagem removida!');
      loadThemeSettings();
    } catch (error) {
      console.error('Erro ao remover imagem:', error);
      alert('Erro ao remover imagem');
    }
  };

  const handleSave = () => {
    setSaving(true);
    
    // Salvar no localStorage (em produção, salvar no backend)
    localStorage.setItem('adminSettings', JSON.stringify({
      boardingPoints,
      departureCities,
      includedOptions,
      notIncludedOptions,
      coupons
    }));

    setTimeout(() => {
      setSaving(false);
      alert('Configurações salvas com sucesso!');
    }, 500);
  };

  // Boarding Points
  const addBoardingPoint = () => {
    const newPoint = prompt('Nome do novo ponto de embarque:');
    if (newPoint && newPoint.trim()) {
      setBoardingPoints([...boardingPoints, newPoint.trim()]);
    }
  };

  const removeBoardingPoint = (index) => {
    setBoardingPoints(boardingPoints.filter((_, i) => i !== index));
  };

  // Departure Cities
  const addDepartureCity = () => {
    const newCity = prompt('Nome da nova cidade de embarque:');
    if (newCity && newCity.trim()) {
      setDepartureCities([...departureCities, newCity.trim()]);
    }
  };

  const removeDepartureCity = (index) => {
    setDepartureCities(departureCities.filter((_, i) => i !== index));
  };

  // Included Options
  const addIncludedOption = () => {
    const newOption = prompt('Nova opção de "Incluso":');
    if (newOption && newOption.trim()) {
      setIncludedOptions([...includedOptions, { text: newOption.trim(), default: false }]);
    }
  };

  const toggleIncludedDefault = (index) => {
    const newOptions = [...includedOptions];
    newOptions[index].default = !newOptions[index].default;
    setIncludedOptions(newOptions);
  };

  const removeIncludedOption = (index) => {
    setIncludedOptions(includedOptions.filter((_, i) => i !== index));
  };

  // Not Included Options
  const addNotIncludedOption = () => {
    const newOption = prompt('Nova opção de "Não Incluso":');
    if (newOption && newOption.trim()) {
      setNotIncludedOptions([...notIncludedOptions, { text: newOption.trim(), default: false }]);
    }
  };

  const toggleNotIncludedDefault = (index) => {
    const newOptions = [...notIncludedOptions];
    newOptions[index].default = !newOptions[index].default;
    setNotIncludedOptions(newOptions);
  };

  const removeNotIncludedOption = (index) => {
    setNotIncludedOptions(notIncludedOptions.filter((_, i) => i !== index));
  };

  // Coupons
  const addCoupon = () => {
    setCoupons([...coupons, { code: '', discount: 0, active: true }]);
  };

  const updateCoupon = (index, field, value) => {
    const newCoupons = [...coupons];
    newCoupons[index] = { ...newCoupons[index], [field]: value };
    setCoupons(newCoupons);
  };

  const removeCoupon = (index) => {
    setCoupons(coupons.filter((_, i) => i !== index));
  };

  // Change Password
  const handlePasswordChange = async () => {
    // Validações
    if (!passwordData.currentPassword) {
      setPasswordMessage({ type: 'error', text: 'Digite a senha atual' });
      return;
    }
    
    // Se não houver novo usuário nem nova senha, erro
    if (!passwordData.newUsername && !passwordData.newPassword) {
      setPasswordMessage({ type: 'error', text: 'Digite novo usuário ou nova senha' });
      return;
    }
    
    // Validar nova senha se fornecida
    if (passwordData.newPassword) {
      if (passwordData.newPassword.length < 6) {
        setPasswordMessage({ type: 'error', text: 'A nova senha deve ter pelo menos 6 caracteres' });
        return;
      }
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordMessage({ type: 'error', text: 'As senhas não conferem' });
        return;
      }
    }

    // Validar novo usuário se fornecido
    if (passwordData.newUsername && passwordData.newUsername.length < 3) {
      setPasswordMessage({ type: 'error', text: 'O usuário deve ter pelo menos 3 caracteres' });
      return;
    }

    try {
      setChangingPassword(true);
      setPasswordMessage({ type: '', text: '' });

      const response = await axios.post(`${BACKEND_URL}/api/auth/change-credentials`, {
        current_password: passwordData.currentPassword,
        new_username: passwordData.newUsername || null,
        new_password: passwordData.newPassword || null
      });

      let successMsg = 'Credenciais alteradas com sucesso!';
      if (response.data.new_username) {
        setCurrentAdminUser(response.data.new_username);
        successMsg = `Usuário alterado para "${response.data.new_username}"!`;
        if (passwordData.newPassword) {
          successMsg += ' Senha também alterada.';
        }
      }

      setPasswordMessage({ type: 'success', text: successMsg });
      setPasswordData({ currentPassword: '', newUsername: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao alterar credenciais';
      setPasswordMessage({ type: 'error', text: errorMsg });
    } finally {
      setChangingPassword(false);
    }
  };

  // Carregar usuário admin atual
  useEffect(() => {
    const loadAdminUser = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/check`);
        if (response.data.username) {
          setCurrentAdminUser(response.data.username);
        }
      } catch (error) {
        console.error('Erro ao carregar usuário admin:', error);
      }
    };
    loadAdminUser();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="hover:text-[#FFD700]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-xl font-bold">Configurações</h1>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar Tudo'}
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Configurações de Tema */}
        <Card className="border-purple-300">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Palette className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-bold text-gray-900">Tema do Site</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Selecione um tema sazonal para aplicar em todo o site.
            </p>
            
            {/* Theme Selector */}
            <div className="mb-6">
              <Label>Tema Ativo</Label>
              <Select value={activeTheme} onValueChange={handleThemeChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(themeDefinitions).map(([id, theme]) => (
                    <SelectItem key={id} value={id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full border"
                          style={{ backgroundColor: theme.primary }}
                        />
                        {theme.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Theme Preview */}
            {activeTheme && themeDefinitions[activeTheme] && (
              <div className="mb-6 p-4 rounded-lg border" style={{ 
                backgroundColor: themeDefinitions[activeTheme].backgroundLight,
                borderColor: themeDefinitions[activeTheme].primary
              }}>
                <p className="text-sm font-medium mb-2" style={{ color: themeDefinitions[activeTheme].primary }}>
                  Preview do Tema: {themeDefinitions[activeTheme].name}
                </p>
                <div className="flex gap-2 flex-wrap">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">Fundo:</span>
                    <div className="w-6 h-6 rounded border" style={{ backgroundColor: themeDefinitions[activeTheme].background }} />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">Primária:</span>
                    <div className="w-6 h-6 rounded border" style={{ backgroundColor: themeDefinitions[activeTheme].primary }} />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">Secundária:</span>
                    <div className="w-6 h-6 rounded border" style={{ backgroundColor: themeDefinitions[activeTheme].secondary }} />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">Rodapé:</span>
                    <div className="w-6 h-6 rounded border" style={{ backgroundColor: themeDefinitions[activeTheme].footerBg }} />
                  </div>
                </div>
              </div>
            )}
            
            {/* Image Uploads */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Image className="w-4 h-4 text-purple-600" />
                <Label>Imagem do Tema</Label>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                PNG transparente. Aparecerá ao lado da logo no header E no centro do rodapé com transparência.
              </p>
              
              {currentThemeConfig?.theme_image ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-600 font-medium">Imagem carregada</span>
                  <Button size="sm" variant="outline" onClick={handleRemoveImage}>
                    <Trash2 className="w-3 h-3 mr-1" /> Remover
                  </Button>
                </div>
              ) : (
                <div>
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={(e) => setThemeImageFile(e.target.files[0])}
                    className="text-sm"
                  />
                  {themeImageFile && (
                    <Button 
                      size="sm" 
                      className="mt-2" 
                      onClick={() => handleImageUpload(themeImageFile)}
                      disabled={uploadingImage}
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      {uploadingImage ? 'Enviando...' : 'Enviar'}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Configurações de Cupom */}
        <Card className="border-orange-300">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Tag className="w-5 h-5 text-orange-600" />
              <h2 className="text-lg font-bold text-gray-900">Configurações de Cupom</h2>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Bloquear cupom durante promoção</p>
                <p className="text-sm text-gray-600">
                  Quando ativado, o campo de cupom será desabilitado enquanto houver pacotes com desconto ativo.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${blockCouponDuringPromotion ? 'text-orange-600' : 'text-gray-500'}`}>
                  {blockCouponDuringPromotion ? 'ON' : 'OFF'}
                </span>
                <button
                  onClick={() => setBlockCouponDuringPromotion(!blockCouponDuringPromotion)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    blockCouponDuringPromotion ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      blockCouponDuringPromotion ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            
            <div className="mt-4">
              <Button 
                onClick={handleSaveSiteSettings}
                disabled={savingSettings}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingSettings ? 'Salvando...' : 'Salvar Configurações'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Pontos de Embarque Rodoviário */}
        <Card>

        {/* ===== MODO CAMPANHA ===== */}
        </Card>
        <Card className="border-purple-300">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Image className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-bold text-gray-900">Modo Campanha (Homepage)</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Ativar modo campanha</p>
                  <p className="text-sm text-gray-600">Substitui o carrossel por uma imagem estática com texto personalizado</p>
                </div>
                <button
                  onClick={() => setCampaignMode(!campaignMode)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${campaignMode ? 'bg-purple-500' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${campaignMode ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              {campaignMode && (
                <div className="space-y-3 pl-2">
                  <div>
                    <Label>Imagem da Campanha</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input 
                        type="file" 
                        accept="image/*" 
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const res = await axios.post(`${BACKEND_URL}/api/themes/upload-campaign-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                            if (res.data?.url) {
                              setCampaignImage(res.data.url);
                            } else {
                              alert('Erro: servidor não retornou URL');
                            }
                          } catch (err) {
                            console.error('Upload error:', err);
                            alert('Erro ao enviar imagem: ' + (err.response?.data?.detail || err.message));
                          }
                        }}
                        className="text-sm"
                      />
                    </div>
                    {campaignImage && (
                      <div className="mt-2 relative">
                        <img src={campaignImage} alt="Preview" className="w-full max-h-40 object-cover rounded-lg" />
                        <button onClick={() => setCampaignImage('')} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">x</button>
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-1">Ou cole a URL diretamente:</p>
                    <Input value={campaignImage} onChange={(e) => setCampaignImage(e.target.value)} placeholder="https://..." className="mt-1 text-sm" />
                  </div>
                  <div>
                    <Label>Texto da Campanha</Label>
                    <Input value={campaignText} onChange={(e) => setCampaignText(e.target.value)} placeholder="Ex: Aniversário TÔ INDO ALI - Descontos de até 30%" className="mt-1" />
                  </div>
                  <div>
                    <Label>Link (opcional)</Label>
                    <Input value={campaignLink} onChange={(e) => setCampaignLink(e.target.value)} placeholder="/pacotes ou URL completa" className="mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Data Início</Label>
                      <Input type="date" value={campaignStart} onChange={(e) => setCampaignStart(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label>Data Fim</Label>
                      <Input type="date" value={campaignEnd} onChange={(e) => setCampaignEnd(e.target.value)} className="mt-1" />
                    </div>
                  </div>

                  {campaignStart && campaignEnd && (() => {
                    const start = new Date(campaignStart + 'T00:00:00');
                    const end = new Date(campaignEnd + 'T00:00:00');
                    if (isNaN(start) || isNaN(end) || end < start) return null;
                    const days = [];
                    const cursor = new Date(start);
                    while (cursor <= end && days.length < 90) {
                      days.push(cursor.toISOString().slice(0, 10));
                      cursor.setDate(cursor.getDate() + 1);
                    }
                    return (
                      <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <Label className="block mb-2 text-sm font-semibold text-purple-900">
                          Desconto por dia da campanha (%)
                        </Label>
                        <p className="text-xs text-purple-700 mb-3">
                          Defina o desconto global que será aplicado automaticamente em cada dia. O sistema aplica sempre o MAIOR desconto entre o tier do carrinho e o desconto do dia.
                        </p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {days.map(date => (
                            <div key={date} className="flex items-center justify-between gap-3 bg-white rounded px-3 py-2">
                              <span className="text-sm text-gray-700 font-medium">
                                {new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                              </span>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="1"
                                  value={campaignDailyDiscounts[date] ?? ''}
                                  onChange={(e) => setCampaignDailyDiscounts(prev => ({ ...prev, [date]: e.target.value === '' ? '' : Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) }))}
                                  placeholder="0"
                                  className="w-20 text-right text-sm h-8"
                                />
                                <span className="text-sm text-gray-500">%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-purple-700 mt-3 italic">
                          ⚠️ Após o fim da campanha, descontos globais são zerados automaticamente.
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}
              <Button onClick={saveCampaignSettings} disabled={savingCampaign} className="bg-purple-600 hover:bg-purple-700 text-white">
                <Save className="w-4 h-4 mr-2" /> {savingCampaign ? 'Salvando...' : 'Salvar Campanha'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ===== INDICADOR DOS CARDS + IMAGEM DA LOGO ===== */}
        <Card className="border-blue-300">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Eye className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-900">Exibição dos Cards e Logo</h2>
            </div>
            <div className="space-y-5">
              {/* Indicador nos cards */}
              <div>
                <Label className="font-semibold">Indicador nos Cards dos Pacotes</Label>
                <p className="text-sm text-gray-500 mb-2">O que aparece ao lado das datas nos cards</p>
                <select
                  value={cardIndicator}
                  onChange={(e) => setCardIndicator(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="card-indicator-select"
                >
                  <option value="percentage">Percentual de vagas vendidas</option>
                  <option value="spots">Quantidade de vagas disponíveis</option>
                  <option value="days">Dias até a viagem (ex: "Faltam 14 dias")</option>
                  <option value="none">Nenhum indicador</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">Aplicado apenas na página individual do pacote.</p>
              </div>

              <Button onClick={async () => {
                try {
                  await axios.put(`${BACKEND_URL}/api/packages/site-settings`, {
                    card_indicator: cardIndicator
                  });
                  alert('Configurações salvas!');
                } catch { alert('Erro ao salvar'); }
              }} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Save className="w-4 h-4 mr-2" /> Salvar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ===== TEMA PERSONALIZADO ===== */}
        <Card className="border-indigo-300">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Palette className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-bold text-gray-900">Criar Tema Personalizado</h2>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Nome do Tema</Label>
                <Input value={customThemeName} onChange={(e) => setCustomThemeName(e.target.value)} placeholder="Ex: Aniversário 2026" className="mt-1" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: 'primary', label: 'Cor Principal' },
                  { key: 'secondary', label: 'Cor Secundária' },
                  { key: 'background', label: 'Fundo' },
                  { key: 'backgroundLight', label: 'Fundo Claro' },
                  { key: 'text', label: 'Texto' },
                  { key: 'headerBg', label: 'Header' }
                ].map(c => (
                  <div key={c.key}>
                    <Label className="text-xs">{c.label}</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="color" value={customThemeColors[c.key]} onChange={(e) => setCustomThemeColors(prev => ({ ...prev, [c.key]: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" />
                      <Input value={customThemeColors[c.key]} onChange={(e) => setCustomThemeColors(prev => ({ ...prev, [c.key]: e.target.value }))} className="text-xs font-mono" />
                    </div>
                  </div>
                ))}
              </div>
              {/* Preview */}
              <div className="p-4 rounded-lg border-2" style={{ backgroundColor: customThemeColors.backgroundLight, borderColor: customThemeColors.primary }}>
                <p className="text-sm font-bold" style={{ color: customThemeColors.primary }}>Preview</p>
                <div className="flex gap-2 mt-2">
                  {Object.entries(customThemeColors).map(([k, v]) => (
                    <div key={k} className="text-center">
                      <div className="w-8 h-8 rounded border" style={{ backgroundColor: v }} />
                      <span className="text-[9px] text-gray-500">{k}</span>
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={saveCustomTheme} disabled={savingCustomTheme} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <Save className="w-4 h-4 mr-2" /> {savingCustomTheme ? 'Salvando...' : 'Salvar Tema'}
              </Button>
              {/* Temas personalizados existentes */}
              {Object.keys(customThemes).length > 0 && (
                <div className="border-t pt-3">
                  <Label className="text-sm font-bold text-gray-700">Temas Salvos</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(customThemes).map(([id, theme]) => (
                      <div key={id} className="flex items-center gap-2 bg-gray-50 border rounded-lg px-3 py-2">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: theme.primary }} />
                        <span className="text-sm">{theme.name}</span>
                        <button onClick={async () => { await axios.delete(`${BACKEND_URL}/api/themes/custom/${id}`); loadCustomThemes(); }} className="text-red-400 hover:text-red-600 text-xs ml-1">x</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pontos de Embarque Rodoviário */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-orange-600" />
                <h2 className="text-lg font-bold text-gray-900">Pontos de Embarque (Rodoviário)</h2>
              </div>
              <Button onClick={addBoardingPoint} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Pontos para viagens rodoviárias.
            </p>
            <div className="space-y-2">
              {boardingPoints.map((point, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <span className="text-gray-800">{point}</span>
                  <Button
                    onClick={() => removeBoardingPoint(index)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Cidades de Embarque Aéreo */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Plane className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-bold text-gray-900">Cidades de Embarque (Aéreo)</h2>
              </div>
              <Button onClick={addDepartureCity} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Cidades para viagens aéreas (nacional e internacional).
            </p>
            <div className="space-y-2">
              {departureCities.map((city, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="text-gray-800">{city}</span>
                  <Button
                    onClick={() => removeDepartureCity(index)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Opções de Incluso */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-bold text-gray-900">Opções de "Incluso"</h2>
              </div>
              <Button onClick={addIncludedOption} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Marque as opções que ficarão selecionadas por padrão ao criar um novo pacote.
            </p>
            <div className="space-y-2">
              {includedOptions.map((option, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={option.default}
                      onCheckedChange={() => toggleIncludedDefault(index)}
                    />
                    <span className="text-gray-800">{option.text}</span>
                  </div>
                  <Button
                    onClick={() => removeIncludedOption(index)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Opções de Não Incluso */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <X className="w-5 h-5 text-red-600" />
                <h2 className="text-lg font-bold text-gray-900">Opções de "Não Incluso"</h2>
              </div>
              <Button onClick={addNotIncludedOption} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Marque as opções que ficarão selecionadas por padrão ao criar um novo pacote.
            </p>
            <div className="space-y-2">
              {notIncludedOptions.map((option, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={option.default}
                      onCheckedChange={() => toggleNotIncludedDefault(index)}
                    />
                    <span className="text-gray-800">{option.text}</span>
                  </div>
                  <Button
                    onClick={() => removeNotIncludedOption(index)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Alterar Credenciais Admin */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Lock className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-bold text-gray-900">Credenciais de Acesso Admin</h2>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Altere o usuário e/ou senha de acesso ao painel administrativo.
            </p>
            <p className="text-sm text-blue-600 mb-4">
              Usuário atual: <strong>{currentAdminUser}</strong>
            </p>

            {passwordMessage.text && (
              <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                passwordMessage.type === 'success' 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                <AlertCircle className="w-4 h-4" />
                {passwordMessage.text}
              </div>
            )}

            <div className="space-y-4 max-w-md">
              <div>
                <Label>Senha Atual <span className="text-red-500">*</span></Label>
                <div className="relative mt-1">
                  <Input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    placeholder="Digite sua senha atual"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label>Novo Usuário (opcional)</Label>
                <Input
                  value={passwordData.newUsername}
                  onChange={(e) => setPasswordData({ ...passwordData, newUsername: e.target.value })}
                  placeholder="Digite novo usuário (mínimo 3 caracteres)"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Nova Senha (opcional)</Label>
                <div className="relative mt-1">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    placeholder="Digite a nova senha (mínimo 6 caracteres)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label>Confirmar Nova Senha</Label>
                <Input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Confirme a nova senha"
                  className="mt-1"
                  disabled={!passwordData.newPassword}
                />
              </div>

              <Button
                onClick={handlePasswordChange}
                disabled={changingPassword}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Lock className="w-4 h-4 mr-2" />
                {changingPassword ? 'Alterando...' : 'Alterar Credenciais'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Manutenção SEO */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Manutenção SEO
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Ferramentas para manter os slugs e SEO dos pacotes funcionando corretamente.
            </p>
            
            <div className="space-y-4">
              {/* Corrigir Slugs Duplicados */}
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h3 className="font-medium text-orange-800 mb-2">Corrigir Slugs Duplicados</h3>
                <p className="text-sm text-orange-700 mb-3">
                  Se você tem pacotes com o mesmo nome mas datas/valores diferentes (ex: "Alter do Chão" em Maio e "Alter do Chão" em Outubro), 
                  execute esta correção para que cada um tenha um slug único (ex: alter-do-chao-maio-2026) e os links funcionem corretamente.
                </p>
                <Button
                  onClick={async () => {
                    if (!window.confirm('Isso irá adicionar mês/ano ao slug de pacotes com nomes duplicados. Continuar?')) return;
                    try {
                      const response = await axios.post(`${BACKEND_URL}/api/seo/fix-duplicate-slugs`);
                      if (response.data.success) {
                        let msg = response.data.message;
                        if (response.data.fixed_packages?.length > 0) {
                          msg += '\n\nPacotes corrigidos:';
                          response.data.fixed_packages.forEach(p => {
                            msg += `\n• ${p.name}: ${p.old_slug} → ${p.new_slug}`;
                          });
                        }
                        alert(msg);
                      } else {
                        alert('Erro ao corrigir slugs');
                      }
                    } catch (error) {
                      alert('Erro: ' + (error.response?.data?.detail || error.message));
                    }
                  }}
                  variant="outline"
                  className="border-orange-500 text-orange-700 hover:bg-orange-100"
                >
                  Verificar e Corrigir Slugs
                </Button>
              </div>

              {/* Backfill SEO */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-medium text-blue-800 mb-2">Regenerar SEO de Todos os Pacotes</h3>
                <p className="text-sm text-blue-700 mb-3">
                  Atualiza os campos SEO (slug, título, descrição) de todos os pacotes. 
                  Use apenas se precisar regenerar SEO em massa.
                </p>
                <Button
                  onClick={async () => {
                    if (!window.confirm('Isso irá regenerar os campos SEO de todos os pacotes. Os campos existentes serão atualizados apenas se estiverem vazios. Continuar?')) return;
                    try {
                      const response = await axios.post(`${BACKEND_URL}/api/seo/backfill-seo?force=false`);
                      alert(`SEO atualizado!\n\nPacotes processados: ${response.data.updated_count || 0}`);
                    } catch (error) {
                      alert('Erro: ' + (error.response?.data?.detail || error.message));
                    }
                  }}
                  variant="outline"
                  className="border-blue-500 text-blue-700 hover:bg-blue-100"
                >
                  Regenerar SEO (Manter Existentes)
                </Button>
                <Button
                  onClick={async () => {
                    if (!window.confirm('⚠️ ATENÇÃO: Isso irá SOBRESCREVER os campos SEO de TODOS os pacotes, incluindo slugs personalizados. Tem certeza?')) return;
                    try {
                      const response = await axios.post(`${BACKEND_URL}/api/seo/backfill-seo?force=true`);
                      alert(`SEO regenerado!\n\nPacotes atualizados: ${response.data.updated_count || 0}`);
                    } catch (error) {
                      alert('Erro: ' + (error.response?.data?.detail || error.message));
                    }
                  }}
                  variant="outline"
                  className="border-red-500 text-red-700 hover:bg-red-100 ml-2"
                >
                  Forçar Regeneração (Sobrescrever)
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fechamento Automático de Vendas */}
        <Card data-testid="closing-days-card">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Fechamento Automático de Vendas</h2>
            <p className="text-sm text-gray-500 mb-4">Quantos dias antes da data da viagem o pacote deixa de aceitar novas reservas. A data continua visível como "LOTADO".</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { key: 'nacional-aereo', label: 'Aéreo Nacional' },
                { key: 'internacional-aereo', label: 'Aéreo Internacional' },
                { key: 'rodoviario', label: 'Rodoviário' },
                { key: 'passeio-1-dia', label: 'Passeio 1 dia' },
              ].map(m => (
                <div key={m.key}>
                  <Label className="text-xs">{m.label}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      min={0}
                      value={salesClosingDays[m.key] ?? 0}
                      onChange={(e) => setSalesClosingDays(prev => ({ ...prev, [m.key]: Math.max(0, Number(e.target.value) || 0) }))}
                      data-testid={`closing-days-${m.key}`}
                    />
                    <span className="text-xs text-gray-500">dias</span>
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={saveClosingSettings} disabled={savingClosing} className="mt-4 bg-emerald-600 hover:bg-emerald-700" data-testid="closing-days-save">
              <Save className="w-4 h-4 mr-1" /> {savingClosing ? 'Salvando…' : 'Salvar regras'}
            </Button>
          </CardContent>
        </Card>

        {/* Informações da Empresa */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Informações da Empresa</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Razão Social</Label>
                <Input value="TÔ INDO ALI - Turismo e Aventura" disabled className="mt-1 bg-gray-100" />
              </div>
              <div>
                <Label>CNPJ</Label>
                <Input value="46.231.427/0001-00" disabled className="mt-1 bg-gray-100" />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input value="(21) 97965-2791" disabled className="mt-1 bg-gray-100" />
              </div>
              <div>
                <Label>E-mail</Label>
                <Input value="contato@toindoali.com.br" disabled className="mt-1 bg-gray-100" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettings;
